import {
  ArrowPathIcon,
  InformationCircleIcon,
  UserPlusIcon,
} from '@heroicons/react/24/outline';
import { Button } from '@praetorian-chariot/ui';
import { CircleX } from 'lucide-react';
import React, { useMemo, useState } from 'react';

import { Modal } from '@/components/Modal';
import { getColumnWidth } from '@/components/table/columns/registry';
import { TanStackTable } from '@/components/table/TanStackTable';
import { Columns } from '@/components/table/types';
import { Tooltip } from '@/components/Tooltip';
import { Container } from '@/components/ui';
import { StatusChip } from '@/components/ui/StatusChip';
import { Agent } from '@/hooks/useAgents';

import { AddUserModal } from './AddUserModal';
import { useUserManagement } from './useUserManagement';

interface UserManagementTabProps {
  agent: Agent;
  taskMonitoring: ReturnType<
    typeof import('../task/useTaskMonitoring').useTaskMonitoring
  >;
  setSelectedTabValue: (value: string) => void;
  isModalOpen: boolean;
}

/**
 * Component for managing Linux users on Aegis agents
 *
 * Migrated from Table to TanStackTable following UsersPage pattern.
 * Migration: 2025-12-06
 *
 * Provides interface for:
 * - Viewing current users on the host
 * - Adding new users with validation
 * - Removing existing users
 * - User status display
 */
interface UserData {
  username: string;
}

export const UserManagementTab: React.FC<UserManagementTabProps> = ({
  agent,
  taskMonitoring: _taskMonitoring,
  setSelectedTabValue,
  isModalOpen,
}) => {
  // Local user management state
  const userManagement = useUserManagement(isModalOpen);

  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [operationInProgress, setOperationInProgress] = useState<{
    type: 'add' | 'delete';
    username?: string;
  } | null>(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState<{
    isOpen: boolean;
    username: string;
  }>({ isOpen: false, username: '' });

  // Get current users from the user management hook
  const currentUsers = userManagement.getCurrentUsers(agent);

  // Handler for successful user addition (called by AddUserModal)
  const handleAddUserSuccess = (taskId?: string) => {
    // Switch to tasks tab if task was queued
    if (taskId) {
      setSelectedTabValue('tasks');
    }
  };

  const handleDeleteUser = (username: string) => {
    setDeleteConfirmation({ isOpen: true, username });
  };

  const confirmDeleteUser = async () => {
    if (!agent || !deleteConfirmation.username) return;

    try {
      setOperationInProgress({
        type: 'delete',
        username: deleteConfirmation.username,
      });
      setDeleteConfirmation({ isOpen: false, username: '' });

      const response = await userManagement.deleteUser(
        agent.client_id,
        deleteConfirmation.username,
        agent.hostname
      );

      // Switch to tasks tab if task was queued
      if (response.taskId) {
        setSelectedTabValue('tasks');
      }
    } catch (error) {
      console.error('Failed to delete user:', error);
      // Error handling is managed by the user management hook
    } finally {
      setOperationInProgress(null);
    }
  };

  const cancelDeleteUser = () => {
    setDeleteConfirmation({ isOpen: false, username: '' });
  };

  const userData: UserData[] = useMemo(
    () => currentUsers.map(username => ({ username })),
    [currentUsers]
  );

  const columns: Columns<UserData> = useMemo(
    () => [
      {
        id: 'username',
        label: 'User',
        ...getColumnWidth('agentUser', 'username'),
        cell: (user: UserData) => (
          <span className="text-chariot-text-primary font-mono text-sm font-medium">
            {user.username}
          </span>
        ),
      },
      {
        id: '',
        label: 'Status',
        ...getColumnWidth('agentUser', 'status'),
        cell: () => <StatusChip label="Active" variant="info" />,
      },
      {
        id: '',
        label: '',
        ...getColumnWidth('agentUser', 'actions'),
        align: 'center',
        cell: (user: UserData) => (
          <Button
            styleType="icon"
            label=""
            data-testid={`delete-user-${user.username}-button`}
            onClick={() => handleDeleteUser(user.username)}
            disabled={
              operationInProgress?.type === 'delete' &&
              operationInProgress?.username === user.username
            }
            aria-label={`Delete user ${user.username}`}
            startIcon={
              operationInProgress?.type === 'delete' &&
              operationInProgress?.username === user.username ? (
                <ArrowPathIcon className="size-5 animate-spin" />
              ) : (
                <CircleX className="fill-chariot-stroke-critical stroke-chariot-background-primary size-5" />
              )
            }
          />
        ),
      },
    ],
    [operationInProgress]
  );

  if (!agent) return null;

  return (
    <div
      className="flex h-full flex-col gap-6"
      data-testid="user-management-tab-content"
    >
      {/* Single Container with Header + Add User inline + Table */}
      <Container
        className="border-chariot-stroke bg-chariot-background-primary flex h-full flex-1 flex-col rounded-md border p-6"
        data-testid="user-management-section"
      >
        {/* Header - ALWAYS visible (matches Tasks/Credentials pattern) */}
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h3 className="text-chariot-text-primary text-lg font-semibold">
              Manage Users
            </h3>
            <Tooltip title="Add Linux users with Praetorian email. Username format: first.last (e.g., john.doe)">
              <div className="text-chariot-text-secondary cursor-help">
                <InformationCircleIcon className="size-4" />
              </div>
            </Tooltip>
          </div>
          {/* Only show Add button when users exist (empty state has its own button) */}
          {userData.length > 0 && (
            <Button
              label="Add User"
              styleType="primary"
              size="sm"
              onClick={() => setShowAddUserModal(true)}
              startIcon={<UserPlusIcon className="size-4" />}
              data-testid="add-user-header-button"
            />
          )}
        </div>

        {/* Users Table */}
        <div className="border-chariot-stroke flex min-h-0 flex-1 flex-col overflow-hidden rounded-md border">
          <TanStackTable<UserData>
            name="agent-users"
            columns={columns}
            data={userData}
            status="success"
            error={null}
            selection={{ hide: true }}
            noData={{
              title: 'No Users Found',
              description: 'Users will appear here after they are created.',
              icon: <UserPlusIcon className="size-12" />,
              action: (
                <Button
                  label="Add User"
                  styleType="primary"
                  size="sm"
                  onClick={() => setShowAddUserModal(true)}
                  startIcon={<UserPlusIcon className="size-4" />}
                  data-testid="add-user-empty-state-button"
                />
              ),
            }}
          />
        </div>
      </Container>

      {/* Add User Modal - extracted to standalone component */}
      <AddUserModal
        isOpen={showAddUserModal}
        onClose={() => setShowAddUserModal(false)}
        agent={agent}
        onSuccess={handleAddUserSuccess}
      />

      {/* Delete User Confirmation Modal */}
      <Modal
        open={deleteConfirmation.isOpen}
        onClose={cancelDeleteUser}
        title="Delete User"
        footer={{
          text:
            operationInProgress?.type === 'delete'
              ? 'Deleting...'
              : 'Delete User',
          styleType: 'primary',
          onClick: confirmDeleteUser,
          disabled: operationInProgress?.type === 'delete',
        }}
      >
        {deleteConfirmation.username && (
          <div>
            Are you sure you want to delete user{' '}
            <strong>{deleteConfirmation.username}</strong> from{' '}
            <strong>{agent?.hostname}</strong>?
          </div>
        )}
      </Modal>
    </div>
  );
};
